import { useEffect, useState } from "react";

import { Button, IconButton, Typography } from "@mui/material";
import moment from "moment";
import { useForm } from "react-hook-form";
import { FaChevronLeft } from "react-icons/fa6";
import { Link, Navigate, useParams } from "react-router-dom";

import { TPaymentRequest } from "../../../@types";
import { apis, callApi } from "../../../api";
import { useAccount } from "../../../hooks/useAccount";
import { CommonInput } from "../../common";
import { Container } from "../../common/Container/Container";

import "./bill_detail.scss";

export type TCancelPaymentRequestFieldValues = {
	reason: string;
};

export function BillDetail() {
	type TState = {
		bill: TPaymentRequest | null;
		loading: boolean;
		isCancelling: boolean;
	};
	const [state, setState] = useState<TState>({
		bill: null,
		loading: true,
		isCancelling: false,
	});

	const { control, reset, handleSubmit } =
		useForm<TCancelPaymentRequestFieldValues>({
			defaultValues: {
				reason: "",
			},
			mode: "onChange",
		});

	const params = useParams();
	const billId = params.billId;

	const { account } = useAccount();

	useEffect(() => {
		const getBill = async () => {
			try {
				if (!billId) {
					return;
				}

				setState((prevState) => ({
					...prevState,
					loading: true,
				}));

				const response = await callApi({
					method: "GET",
					url: `${apis.BILL}/${billId}`,
				});

				setState((prevState) => ({
					...prevState,
					bill: response.bill,
					loading: false,
				}));
			} catch (error) {
				setState((prevState) => ({ ...prevState, loading: false }));
			}
		};

		if (billId) {
			getBill();
		}
	}, [billId]);

	const onClickPayNow = async () => {
		try {
			const response = await callApi({
				method: "PATCH",
				url: `${apis.BILL}/${billId}`,
			});

			if (response?.paymentResult?.url) {
				window.location.href = response.paymentResult.url;
			}
		} catch (error) {}
	};

	const onClickCancelPaymentRequest = () => {
		setState((prevState) => ({
			...prevState,
			isCancelling: true,
		}));
	};

	const onClickCancelCancelPaymentRequest = () => {
		reset();
		setState((prevState) => ({
			...prevState,
			isCancelling: false,
		}));
	};

	const onClickSubmitCancelRequest = async (
		data: TCancelPaymentRequestFieldValues,
	) => {
		try {
			const response = await callApi({
				method: "PATCH",
				url: `${apis.BILL}/${billId}/cancel`,
				body: {
					cancelReason: data.reason.trim(),
				},
			});

			if (!response) {
				return;
			}
			setState((prevState) => {
				let bill: TPaymentRequest | null = null;
				if (prevState.bill) {
					bill = {
						...prevState.bill,
						status: "Cancelled",
						cancelReason: data.reason.trim(),
					};
				}
				return {
					...prevState,
					isCancelling: false,
					bill,
				};
			});
		} catch (error) {}
	};

	return (
		<Container className="bill-detail-container">
			{billId && state.bill && !state.loading && (
				<>
					<div className="back-container">
						<Link to="/training/payments">
							<IconButton className="back-icon">
								<FaChevronLeft className="normal-icon" />
							</IconButton>
						</Link>
						<Typography className="normal-text">
							Your payment requests
						</Typography>
					</div>
					<div className="bill-container">
						<Typography className="normal-text">
							{"Course: "}
							<Typography component="span" className="normal-text bold">
								{state.bill.summary.course.name}
							</Typography>
						</Typography>
						{state.bill.userId === account?.id && (
							<Typography className="normal-text">
								{"Instructor: "}
								<Typography component="span" className="normal-text bold">
									{`${state.bill.teacher.firstName} ${state.bill.teacher.lastName}`}
								</Typography>
							</Typography>
						)}
						{state.bill.teacherId === account?.id && (
							<Typography className="normal-text">
								{"Student: "}
								<Typography component="span" className="normal-text bold">
									{`${state.bill.user.firstName} ${state.bill.user.lastName}`}
								</Typography>
							</Typography>
						)}
						<Typography className="normal-text">
							{"Total cost: "}
							<Typography component="span" className="normal-text bold">
								{`$${state.bill.totalCost / 100}`}
							</Typography>
						</Typography>
						<Typography className="normal-text">
							{"Message: "}
							<Typography component="span" className="normal-text bold">
								{state.bill.message}
							</Typography>
						</Typography>
						<Typography className="normal-text">
							{"Requested at: "}
							<Typography component="span" className="normal-text bold">
								{moment(state.bill.createdAt).format("MMM DD YYYY, HH:mm")}
							</Typography>
						</Typography>
						{state.bill.status === "Cancelled" && state.bill.cancelReason && (
							<Typography className="normal-text">
								{"Cancellation reason: "}
								<Typography component="span" className="normal-text bold">
									{state.bill.cancelReason}
								</Typography>
							</Typography>
						)}
						{state.bill.userId === account?.id &&
							state.bill.status === "Issued" && (
								<Button
									className="normal-button primary-button pay-button"
									onClick={onClickPayNow}>
									Send payment
								</Button>
							)}
						{state.bill.teacherId === account?.id &&
							state.bill.status === "Issued" &&
							!state.isCancelling && (
								<Button
									className="normal-button disabled-button cancel-request-button"
									onClick={onClickCancelPaymentRequest}>
									Cancel request
								</Button>
							)}
						{state.bill.teacherId === account?.id &&
							state.bill.status === "Issued" &&
							state.isCancelling && (
								<>
									<div className="separator" />
									<Typography className="normal-text bold">
										Cancellation reason
									</Typography>
									<CommonInput
										control={control}
										name="reason"
										placeholder="Cancellation reason"
										rules={{
											required: {
												value: true,
												message: "Cancellation reason is required.",
											},
											validate: (value) => {
												if (value.trim().length === 0) {
													return "Cancellation reason is required.";
												}
											},
										}}
									/>
									<div className="cancel-request-button-group">
										<Button
											className="normal-button disabled-button cancel-button"
											onClick={onClickCancelCancelPaymentRequest}>
											Cancel
										</Button>
										<Button
											className="normal-button primary-button cancel-button"
											onClick={handleSubmit(onClickSubmitCancelRequest)}>
											Cancel request
										</Button>
									</div>
								</>
							)}
						{state.bill.status === "Issued" && (
							<div className="unpaid">
								<Typography className="normal-text">UNPAID</Typography>
							</div>
						)}
						{state.bill.status === "Paid" && (
							<div className="paid">
								<Typography className="normal-text">PAID</Typography>
							</div>
						)}
						{state.bill.status === "Cancelled" && (
							<div className="cancelled">
								<Typography className="normal-text">CANCELLED</Typography>
							</div>
						)}
					</div>
				</>
			)}
			{!state.bill && !state.loading && <Navigate to="/payments" />}
		</Container>
	);
}
